import React from "react";

/**
 * Shared Yes/No-style "Answer Button" presentation for the workflow forms
 * (YES_NO_FREE_TEXT, CONFIRM_ON_FILE, …). Extracted so every workflow template
 * renders an identical button; matches Figma "Answer Button" (21:5631 / 21:5632).
 *
 * Resting state: neutral #A1B1C8 border, outer drop shadow + a subtle inset
 * highlight over the #FCFCFC fill. Selected state keeps the on-theme primary
 * fill with just the outer drop shadow (the inset highlight is tuned for the
 * light fill).
 *
 * (The legacy Bool component keeps its own local button styling — that is
 * refined separately in IHA-382 — so it is intentionally NOT migrated here.)
 */
export const answerButtonClass = (selected: boolean): string =>
  `flex items-center justify-center gap-2 w-full min-h-[44px] h-16 rounded-2xl border text-base font-semibold transition-all duration-200 active:scale-95 ${
    selected
      ? "bg-primary text-accent border-primary shadow-[0px_2px_2px_rgba(0,0,0,0.15)]"
      : "bg-[#FCFCFC] text-primary border-[#A1B1C8] shadow-[0px_2px_2px_rgba(0,0,0,0.15),inset_0px_-2px_2px_0px_rgba(132,153,131,0.5),inset_0px_0px_2px_0px_#FCFCFC]"
  }`;

/**
 * Full-width primary CTA ("Save & Continue") shared by the workflow edit steps:
 * on-theme green fill, leading ✓, dimmed when disabled.
 */
export const saveButtonClass =
  "flex items-center justify-center gap-2 w-full min-h-[44px] h-16 rounded-2xl bg-primary text-accent font-semibold disabled:opacity-30";

// Glyph paths: ✓ (Yes / Correct / Save & Continue), ✕ (No / Changed),
// ‹ (Change my answer — steps back to the root choice).
export const CHECK_PATH = "M5 13l4 4L19 7";
export const CLOSE_PATH = "M6 6l12 12M18 6L6 18";
export const BACK_PATH = "M15 18l-6-6 6-6";

/** Leading button glyph (inline SVG; currentColor follows the button text). */
export const AnswerIcon: React.FC<{ testId: string; path: string }> = ({
  testId,
  path,
}) => (
  <svg
    aria-hidden
    data-testid={testId}
    viewBox="0 0 24 24"
    className="h-6 w-6 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={path} />
  </svg>
);
